import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useLazyGetClientServiceByIdQuery,
  useLazyGetClientServiceCategoriesForFilterQuery,
  useSearchClientServicesMutation,
  useUpdateClientServiceCategoryMutation,
  useUnarchiveClientServiceMutation,
  useUpdateClientServiceMutation,
} from '@app/api/ClientServices';
import { SUCCESS, WARNING, CLIENT_SERVICES_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { usePageContext } from '@app/providers/PageProvider/usePageContext';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NEmployees } from '@app/types';
import type { NClientServices } from '@app/types/ClientServices';

import type { BaseOptionType } from 'antd/es/select';

const { useForm } = Form;

export const useClientServiceParams = () => {
  const { activeBranchId } = useUserPrint();
  const [form] = useForm();
  const [isLoader, setLoader] = useState(false);
  const [searchParams] = useSearchParams();
  const serviceId = Number(searchParams.get('id'));
  const navigate = useNavigate();

  const [unarchiveClientService, { isLoading: isRestoring }] =
    useUnarchiveClientServiceMutation();
  const [getClientServiceById] = useLazyGetClientServiceByIdQuery();
  const [getClientServiceCategories] =
    useLazyGetClientServiceCategoriesForFilterQuery();
  const [searchClientServices] = useSearchClientServicesMutation();
  const [updateClientService] = useUpdateClientServiceMutation();
  const [updateClientServiceCategory] =
    useUpdateClientServiceCategoryMutation();

  const { showNotification } = useNotification();
  const { setTitleData } = usePageContext();

  const [serverError, setServerError] = useState('');
  const [isRange, setRange] = useState(false);
  const [categories, setCategories] = useState<BaseOptionType[]>([]);
  const [categoriesLoader, setCategoriesLoader] = useState(false);
  const [modalForm] = useForm();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [selectedAdditions, setSelectedAdditions] = useState<BaseOptionType[]>(
    []
  );
  const [allAdditions, setAllAdditions] = useState<NClientServices.Service[]>(
    []
  );
  const [additionsForm] = useForm();

  const {
    userAccessRights: { ClientServices },
  } = useNewUserAccessRights();

  const hasCreateServiceCategoryRight = useMemo(
    () => ClientServices.IsCategoryCreate,
    [ClientServices]
  );

  const hasDeleteServiceRight = useMemo(
    () => ClientServices.IsDelete,
    [ClientServices]
  );

  const hasEditServiceRight = useMemo(
    () => ClientServices.IsEdit,
    [ClientServices]
  );

  useEffect(() => {
    return () => setTitleData('');
  }, [setTitleData]);

  const [isCategorySelect, setCategorySelect] = useState(false);

  const initialValues = useMemo(() => {
    const {
      Category = null,
      Duration = null,
      Hour0 = 0,
      Minute0 = 0,
      Name = '',
      Description = '',
      PriceE = '',
      PriceS = '',
      Range = false,
    } = form.getFieldsValue();
    return {
      Category,
      Duration,
      Hour0,
      Minute0,
      Name,
      PriceE,
      PriceS,
      Range,
      Description,
    };
  }, [form]);

  const isEditMode = useMemo(() => Boolean(serviceId), [serviceId]);

  const adaptAdditionsToClient = (
    additions: NEmployees.ModelClientService['ClientServiceAdditions']
  ) => {
    const result = (additions || []).map((item) => ({
      label: item.Name,
      value: item.AdditionServiceId,
    }));
    return result;
  };

  const fetchClientService = useCallback(async () => {
    if (!activeBranchId || !serviceId) return;
    if (isEditMode) {
      setLoader(true);
      try {
        const {
          Name,
          PriceS,
          PriceE,
          CategoryId,
          Duration,
          ServiceType,
          ClientServiceAdditions,
          PublicName,
          Description,
          Id,
          MedicalServiceCode,
          IsDeleted,
        } = await getClientServiceById({
          ActiveBranchId: activeBranchId,
          serviceId,
        }).unwrap();
        setTitleData(`Редактирование услуги #${Id}`);
        setIsArchived(IsDeleted);
        const initialAdditions = adaptAdditionsToClient(ClientServiceAdditions);
        setSelectedAdditions(initialAdditions || []);
        initialAdditions?.forEach(({ label }, index) =>
          additionsForm.setFieldValue(`Addition${index}`, label)
        );
        const hours = Math.floor(Duration / 60);
        form.setFieldsValue({
          Name,
          PriceS: PriceS.toString(),
          PriceE: PriceE?.toString() || '',
          Category: CategoryId,
          Hour0: hours,
          Minute0: Duration - hours * 60,
          ServiceType,
          PublicName: PublicName || '',
          Description,
          MedicalServiceCode,
        });
        PriceE && setRange(true);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    } else {
      form.setFieldsValue({
        ...form.getFieldsValue(),
        Hour0: 0,
        Minute0: 0,
      });
    }
  }, [
    isEditMode,
    activeBranchId,
    serviceId,
    setTitleData,
    form,
    additionsForm,
    showNotification,
    getClientServiceById,
  ]);

  useEffect(() => {
    fetchClientService();
  }, [fetchClientService]);

  const fetchClientServiceCategories = useCallback(async () => {
    setCategoriesLoader(true);
    try {
      const data = await getClientServiceCategories({
        ActiveBranchId: activeBranchId,
      }).unwrap();
      const adaptedForSelect = data.map(({ Id, Name }) => ({
        label: generateSpanWithDataTestId(Name, 'category-item'),
        value: Id,
      }));
      setCategories(adaptedForSelect);
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setCategoriesLoader(false);
    }
  }, [
    activeBranchId,
    setCategoriesLoader,
    setCategories,
    showNotification,
    getClientServiceCategories,
  ]);

  useEffect(() => {
    fetchClientServiceCategories();
  }, [fetchClientServiceCategories]);

  const fetchClientServices = useCallback(async () => {
    try {
      const data = await searchClientServices({
        ActiveBranchId: activeBranchId,
        data: {
          SearchTerm: null,
          CategoryIds: categories?.map((item) => item.value || item.Id) || [],
          BranchIds: null,
        },
      }).unwrap();
      setAllAdditions(data);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [activeBranchId, categories, showNotification, searchClientServices]);

  useEffect(() => {
    fetchClientServices();
  }, [fetchClientServices]);

  const findFullObjectsOfAdditions = useCallback(
    (selectedAdditions: BaseOptionType[]) => {
      const result: NClientServices.Service[] = [];
      allAdditions.forEach((item) => {
        selectedAdditions.forEach((_item) => {
          if (item.Id === _item.value) {
            return result.push(item);
          }
        });
      });
      const adaptedAdditions = result.map((item: NClientServices.Service) => ({
        Id: 0,
        ServiceId: serviceId,
        AdditionServiceId: item.Id,
        Name: item.Name,
      }));
      return adaptedAdditions;
    },
    [allAdditions, serviceId]
  );

  const parsePrice = useCallback(
    (price: number | string) => parseFloat(String(price).replace(',', '.')),
    []
  );

  const getSavedData = useCallback(
    ({ IsDeleted }: { IsDeleted: boolean }) => {
      const data = form.getFieldsValue();
      const {
        Name,
        Category,
        ServiceType,
        PriceS,
        Hour0,
        Minute0,
        PriceE,
        CountEmployees,
        PublicName,
        SalaryFormat,
        SalaryAmount,
        Description,
        MedicalServiceCode,
      } = data;

      const handledDuration = Hour0 * 60 + Minute0;
      const categoryName =
        categories?.find(({ Id }) => Id === Category)?.Name || null;

      const adaptedData = {
        Id: serviceId || 0,
        BranchId: activeBranchId || 0,
        Name,
        Category: categoryName,
        CategoryId: Category,
        ServiceType,
        PriceS: parsePrice(PriceS),
        PriceE: PriceE ? parsePrice(PriceE) : null,
        Duration: handledDuration,
        IsDeleted,
        ClientServiceAdditions: findFullObjectsOfAdditions(selectedAdditions),
        CountEmployees: CountEmployees,
        PublicName: PublicName || Name,
        SalaryFormat,
        SalaryAmount,
        Description,
        MedicalServiceCode,
      };

      return adaptedData;
    },
    [
      activeBranchId,
      categories,
      findFullObjectsOfAdditions,
      form,
      parsePrice,
      selectedAdditions,
      serviceId,
    ]
  );

  const sendDataToDb = useCallback(async () => {
    if (activeBranchId) {
      setLoader(true);

      const data = getSavedData({ IsDeleted: false });

      try {
        const { Id } = await updateClientService({
          ActiveBranchId: activeBranchId,
          data,
        }).unwrap();

        showNotification('Данные сохранены успешно', SUCCESS);

        const additionalURL = serviceId ? '' : `?clientServiceId=${Id}`;
        navigate(`${CLIENT_SERVICES_PATH}${additionalURL}`);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setCategoriesLoader(false);
        setLoader(false);
      }
    }
  }, [
    activeBranchId,
    getSavedData,
    showNotification,
    serviceId,
    navigate,
    updateClientService,
  ]);

  const sendNewCategoryToDb = useCallback(async () => {
    const adaptedData = {
      Id: 0,
      Name: modalForm.getFieldsValue().Name,
      Description: modalForm.getFieldsValue().Description,
      BranchId: activeBranchId || 0,
      IsDeleted: false,
      MigrateId: null,
    };
    setCategoriesLoader(true);
    try {
      const { Id } = await updateClientServiceCategory({
        ActiveBranchId: activeBranchId,
        data: adaptedData,
      }).unwrap();
      const newCategory = {
        label: generateSpanWithDataTestId(
          modalForm.getFieldsValue().Name,
          'category-item'
        ),
        value: Id,
      };
      setCategories((oldCategories) => [...oldCategories, newCategory]);
      form.setFieldValue('Category', newCategory.value);
      setIsCreateModalOpen(false);
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setCategoriesLoader(false);
    }
  }, [
    activeBranchId,
    modalForm,
    form,
    setCategoriesLoader,
    setCategories,
    setIsCreateModalOpen,
    showNotification,
    updateClientServiceCategory,
  ]);

  const handleDeleteClientService = useCallback(async () => {
    try {
      setLoader(true);
      const data = getSavedData({ IsDeleted: true });
      await updateClientService({
        ActiveBranchId: activeBranchId,
        data,
      }).unwrap();

      showNotification('Услуга удалена успешно', 'success');
      navigate(CLIENT_SERVICES_PATH);
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setIsDeleteModalOpen(false);
      setLoader(false);
    }
  }, [
    activeBranchId,
    getSavedData,
    navigate,
    showNotification,
    updateClientService,
  ]);

  const handleRestoreClientService = useCallback(async () => {
    if (!serviceId || !activeBranchId) return;

    try {
      await unarchiveClientService({
        serviceId,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      showNotification('Услуга возвращена из архива', SUCCESS);
      navigate(CLIENT_SERVICES_PATH);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [
    serviceId,
    activeBranchId,
    unarchiveClientService,
    showNotification,
    navigate,
  ]);

  return {
    clientId: serviceId,
    form,
    isLoader,
    serverError,
    isRange,
    categories,
    categoriesLoader,
    modalForm,
    isCreateModalOpen,
    setIsCreateModalOpen,
    setRange,
    sendDataToDb,
    setServerError,
    sendNewCategoryToDb,
    initialValues,
    isCategorySelect,
    hasCreateServiceCategoryRight,
    setCategorySelect,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleDeleteClientService,
    hasDeleteServiceRight,
    isEditMode,
    isArchived,
    isRestoring,
    hasEditServiceRight,
    handleRestoreClientService,
  };
};
